import axios, { InternalAxiosRequestConfig } from 'axios'
import { supabase } from '../supabase'

import { 
  UserResponse, 
  VerifyPasswordRequest, 
  VerifyPasswordResponse, 
  ChangePasswordRequest, 
  ChangePasswordResponse 
} from './generated/models'

export type User = UserResponse

export interface SupabaseUser extends User {
  app_metadata: {
    provider?: string
    [key: string]: any
  }
  user_metadata: {
    [key: string]: any
  }
  created_at: string
  updated_at: string
  phone?: string | null
  confirmed_at?: string | null
  email_confirmed_at?: string | null
  banned_until?: string | null
  aud: string
}

const api = axios.create({
  // In development, use relative URLs to leverage the Vite proxy
  baseURL: import.meta.env.DEV 
    ? '' // Empty string means use relative URLs
    : (import.meta.env.VITE_API_URL || 'http://localhost:8080'),
  headers: {
    'Content-Type': 'application/json',
  },
  // Always include credentials for cross-origin requests
  withCredentials: true,
  // Add timeout to prevent hanging requests
  timeout: 300000, // 5 minutes (300 seconds)
})

// Configure axios instance for all API calls
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Handle 401 Unauthorized responses by redirecting to login
      console.log('[API] 401 Unauthorized detected, redirecting to login page')
      
      // Clear the session from Supabase
      try {
        await supabase.auth.signOut()
      } catch (signOutError) {
        console.error('[API] Error during signOut:', signOutError)
      }
      
      // Redirect to login page using hash routing
      window.location.hash = '/auth/login'
      
      return Promise.reject(error)
    }
    
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      // Trigger a session refresh to update ban status
      const auth = (window as any).__SUPABASE_AUTH_PROVIDER__
      if (auth?.checkBanStatus) {
        await auth.checkBanStatus()
      }
    }
    return Promise.reject(error)
  }
)

export const getPlatformAdminUsers = async (): Promise<User[]> => {
  const response = await api.get('/api/v1/users?role=platform_admin')
  return response.data.items
}

export const getClubUsers = async (clubId: string): Promise<User[]> => {
  const response = await api.get(`/api/v1/users?entityId=${clubId}`)
  return response.data.items
}

export const getUsers = async (): Promise<User[]> => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (!token) {
    throw new Error('No authentication token available')
  }

  // In development, use relative URLs to leverage the Vite proxy
  const url = import.meta.env.DEV
    ? '/auth/v1/admin/users'
    : `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/admin/users`;
    
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    withCredentials: true,
  })

  return response.data
}

export const getUserProfile = async () => {
  const response = await api.get('/api/v1/users/profile')
  return response.data
}

export const deleteTotpDevice = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')
  await api.delete(`/api/v1/users/${user.id}/totp`)
}

export const inviteUser = async (data: {
  email: string
  firstName: string
  lastName: string
  userRole?: string
  role?: string
  entityType?: string
  entityId?: string
  redirectUrl?: string
}): Promise<void> => {
  await api.post('/api/v1/users', data)
}

export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/api/v1/users/${userId}`)
}

export const getUserById = async (userId: string): Promise<User> => {
  const response = await api.get(`/api/v1/users/${userId}`)
  return response.data
}

export const resendInvite = async (userId: string): Promise<void> => {
  await api.post(`/api/v1/users/${userId}/resend-invite`)
}

/**
 * Verifies the current user's password
 * @param password The password to verify
 * @returns A promise that resolves to a VerifyPasswordResponse
 */
export const verifyPassword = async (password: string): Promise<VerifyPasswordResponse> => {
  const request: VerifyPasswordRequest = { password }
  const response = await api.post('/api/v1/users/verify-password', request)
  return response.data
}

/**
 * Changes the current user's password
 * @param currentPassword The current password
 * @param newPassword The new password
 * @returns A promise that resolves to a ChangePasswordResponse
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
  const request: ChangePasswordRequest = { currentPassword, newPassword }
  const response = await api.post('/api/v1/users/change-password', request)
  return response.data
}

export { api }
